import i18n from "@/i18n.ts";
import {
  PerformanceReviewEditPageProps,
  usePerformanceReviewEditPage,
} from "@/pages/talent/performance-reviews/edit/pages/performance-review-edit-pages.tsx";
import { PerformanceReviewTitleSlot } from "@/pages/talent/performance-reviews/edit/pages/PerformanceReviewTitleSlot.tsx";
import {
  PerformanceReviewEditPageStep,
  ReviewerTypeItem,
} from "@/pages/talent/performance-reviews/edit/performance-review-edit.ts";
import {
  Box,
  Button,
  FullScreenPage,
  Skeleton,
  Stack,
  Title,
  Switch,
  useModals,
} from "@skailer-com/uikit";
import { PerformanceReviewEditSidebar } from "@/pages/talent/performance-reviews/edit/components/PerformanceReviewEditSidebar.tsx";
import { useTranslation } from "react-i18next";
import {
  usePerformanceReviewerPreviewCount,
  usePerformanceReviewSaveReviewerTypes,
} from "@/queries/performance-reviews/use-performance-reviews.query.ts";
import {
  ApiPerformanceReviewReviewerTypes,
  ApiPerformanceReviewSaveReviewerType,
  PerformanceReviewReviewerTypeItems,
} from "@/types/performance-review.types.ts";
import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { PerformanceReviewReviewerTypeAddOrEditModal } from "@/pages/talent/performance-reviews/edit/widgets/PerformanceReviewReviewerTypeAddOrEditModal.tsx";

export interface ReviewerTypesFormData {
  reviewerTypes: Array<ReviewerTypeItem & { isEnabled: boolean }>;
}

const getInitialFormData = ({ review }: PerformanceReviewEditPageProps): ReviewerTypesFormData => {
  const defaultReviewerTypeItems = [
    {
      id: null,
      title:
        PerformanceReviewReviewerTypeItems().find(
          (x) => x.id == ApiPerformanceReviewReviewerTypes.myself
        )?.title ?? null,
      type: ApiPerformanceReviewReviewerTypes.myself,
    },
    {
      id: null,
      title:
        PerformanceReviewReviewerTypeItems().find(
          (x) => x.id == ApiPerformanceReviewReviewerTypes.manager
        )?.title ?? null,
      type: ApiPerformanceReviewReviewerTypes.manager,
    },
    {
      id: null,
      title:
        PerformanceReviewReviewerTypeItems().find(
          (x) => x.id == ApiPerformanceReviewReviewerTypes.upward
        )?.title ?? null,
      type: ApiPerformanceReviewReviewerTypes.upward,
    },
    {
      id: null,
      title:
        PerformanceReviewReviewerTypeItems().find(
          (x) => x.id == ApiPerformanceReviewReviewerTypes.peer
        )?.title ?? null,
      description: i18n.t("performance_review:review_type_peer_description"),
      type: ApiPerformanceReviewReviewerTypes.peer,
    },
  ] as ReviewerTypeItem[];

  const systemTypes = defaultReviewerTypeItems.map((item) => {
    const reviewerType = review.reviewer_types.find((r) => r.type === item.type);
    return {
      ...item,
      id: reviewerType?.id || null,
      isEnabled: !!reviewerType,
    };
  });

  const customTypes = review.reviewer_types
    .filter((item) => item.type === ApiPerformanceReviewReviewerTypes.custom)
    .map((item) => ({
      id: item.id,
      title: item.title,
      description: i18n.t("performance_review:review_type_peer_description"),
      type: item.type,
      isEnabled: true,
    }));

  return {
    reviewerTypes: [...systemTypes, ...customTypes],
  };
};

export const PerformanceReviewEditReviewerTypesPage = ({
  review,
}: PerformanceReviewEditPageProps) => {
  const { t } = useTranslation();
  const { confirmExit, navigateToStep } = usePerformanceReviewEditPage();

  const saveReviewerTypes = usePerformanceReviewSaveReviewerTypes();
  const { data: reviewerPreviewCount, isLoading: previewCountIsLoading } =
    usePerformanceReviewerPreviewCount(review.id);

  const reviewerCountByType = useMemo(() => {
    return Object.fromEntries(
      (reviewerPreviewCount ?? []).map((item) => [item.reviewer_type, item.count])
    );
  }, [reviewerPreviewCount]);

  const { openModal } = useModals();

  const reviewerTypesSchema = yup.object().shape({
    reviewerTypes: yup
      .array()
      .of(
        yup.object().shape({
          id: yup.number().nullable().defined(),
          title: yup.string().nullable().defined(),
          type: yup.mixed<ApiPerformanceReviewReviewerTypes>().required(),
          isEnabled: yup.boolean().required(),
        })
      )
      .test("at-least-one-enabled", t("errors:reviewers_validation_error"), (value) => {
        return value ? value.some((item) => item.isEnabled) : false;
      })
      .test(
        "reviewer_types_limit_exceeded",
        t("api_errors:reviewer_types_limit_exceeded"),
        (value) => (value ? value.length <= 8 : true)
      )
      .required(),
  });

  const form = useForm<ReviewerTypesFormData>({
    resolver: yupResolver(reviewerTypesSchema),
    defaultValues: getInitialFormData({ review: review }),
    mode: "onChange",
  });

  const {
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = form;
  const reviewerTypesValues = watch("reviewerTypes");

  useEffect(() => {
    const initialData = getInitialFormData({ review: review });
    form.reset(initialData);
  }, [review.reviewer_types, form]);

  const onSubmit = handleSubmit(async (data) => {
    const enabledTypes = data.reviewerTypes.filter((item) => item.isEnabled);

    try {
      const saveTypes: ApiPerformanceReviewSaveReviewerType[] = enabledTypes.map((item) => ({
        id: item.id,
        title: item.type === ApiPerformanceReviewReviewerTypes.custom ? item.title : null,
        type: item.type,
      }));

      await saveReviewerTypes.mutateAsync({
        id: review.id,
        payload: { types: saveTypes },
      });

      navigateToStep(review.id, PerformanceReviewEditPageStep.forms);
    } catch {}
  });

  const handleToggleReviewerType = (index: number) => {
    const currentValue = reviewerTypesValues[index];
    setValue(`reviewerTypes.${index}.isEnabled`, !currentValue.isEnabled, {
      shouldValidate: true,
    });
  };

  const handleReviewerTypeAdd = () => {
    openModal(PerformanceReviewReviewerTypeAddOrEditModal, {
      onSave: async (addItem: ReviewerTypeItem) => {
        const newReviewerType = { ...addItem, isEnabled: true };
        setValue("reviewerTypes", [...reviewerTypesValues, newReviewerType], {
          shouldValidate: true,
        });
      },
    });
  };

  const handleReviewerTypeEdit = (index: number, reviewerTypeItem: ReviewerTypeItem) => {
    if (reviewerTypeItem.type !== ApiPerformanceReviewReviewerTypes.custom) return;

    openModal(PerformanceReviewReviewerTypeAddOrEditModal, {
      reviewerTypeItem: reviewerTypeItem,
      onSave: async (editItem: ReviewerTypeItem) => {
        const updatedReviewerTypes = [...reviewerTypesValues];
        updatedReviewerTypes[index] = {
          ...editItem,
          isEnabled: reviewerTypesValues[index].isEnabled,
        };
        setValue("reviewerTypes", updatedReviewerTypes, { shouldValidate: true });
      },
    });
  };

  return (
    <FullScreenPage
      backSlot={
        <Button variant={"secondary"} size={"lg"} onClick={() => confirmExit(review.id)}>
          {t("common:exit")}
        </Button>
      }
      actionsSlot={
        <>
          <Button
            variant={"secondary"}
            size={"lg"}
            onClick={() => {
              navigateToStep(review.id, PerformanceReviewEditPageStep.details);
            }}
          >
            {t("common:back")}
          </Button>
          <Button size={"lg"} onClick={onSubmit}>
            {t("common:continue")}
          </Button>
        </>
      }
      titleSlot={
        <PerformanceReviewTitleSlot
          step={PerformanceReviewEditPageStep.reviewers}
          review={review}
        />
      }
      isCenterVertical={false}
      sidebarSlot={
        <PerformanceReviewEditSidebar
          step={PerformanceReviewEditPageStep.reviewers}
          review={review}
        />
      }
    >
      <Stack gap={"sm"} justify={"center"}>
        <Stack className={"card-panel"} gap={"lg"}>
          <Title
            header={t("performance_review:reviewers")}
            caption={t("performance_review:reviewers_description")}
            size={"lg"}
          />

          <Stack gap={"lg"}>
            {reviewerTypesValues.map((item, index) => {
              const isCustom = item.type === ApiPerformanceReviewReviewerTypes.custom;

              const reviewerCount = reviewerCountByType[item.type] ?? undefined;

              let description = "";
              let countIsLoading = previewCountIsLoading;
              if (isCustom) {
                description = t("performance_review:review_type_peer_description");
                countIsLoading = false;
              } else if (item.description) {
                description = item.description;
                countIsLoading = false;
              } else if (reviewerCount !== undefined) {
                description = t("plurals:people", { count: reviewerCount });
              }

              return (
                <Stack
                  key={`reviewer-item-${item.type}-${item.id ?? ""}`}
                  gap={"md"}
                  className={"p-4 border border-dark/5 rounded-xs bg-dark/5"}
                >
                  <Stack direction={"horizontal"} justify={"between"} gap={"md"} items={"center"}>
                    <Box>
                      <Switch
                        title={item.title ?? ""}
                        checked={item.isEnabled}
                        description={description}
                        onChange={() => handleToggleReviewerType(index)}
                      />
                      {countIsLoading && <Skeleton className="ml-10 w-30 h-4" />}
                    </Box>

                    {isCustom && (
                      <Button
                        className="shrink-0"
                        variant={"secondary"}
                        onClick={() => {
                          handleReviewerTypeEdit(index, item);
                        }}
                      >
                        {t("common:edit")}
                      </Button>
                    )}
                  </Stack>
                </Stack>
              );
            })}
            <Stack direction={"horizontal"}>
              {reviewerTypesValues.length < 8 && (
                <Button
                  variant={"secondary"}
                  onClick={() => {
                    handleReviewerTypeAdd();
                  }}
                >
                  {t("performance_review:add_reviewer_type")}
                </Button>
              )}
            </Stack>
          </Stack>

          {errors.reviewerTypes?.message && (
            <Box className="pt-2 pb-4">
              <span className="text-danger">{errors.reviewerTypes?.message}</span>
            </Box>
          )}
        </Stack>
        <Button onClick={onSubmit} size={"lg"} isLoading={saveReviewerTypes.isPending}>
          {t("common:continue")}
        </Button>
      </Stack>
    </FullScreenPage>
  );
};
